import { styled } from '@linaria/react';
import type { KeyboardEvent } from 'react';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';

type StepKind = 'trigger' | 'action' | 'delivery';

interface AgentAutomationStepCardProps {
  stepNumber: number;
  kind: StepKind;
  kindLabel: string;
  title: string;
  subtitle?: string | null;
  iconName: string;
  iconColor?: string;
  iconBackground?: string;
  configured: boolean;
  selected: boolean;
  onSelect: () => void;
}

export function AgentAutomationStepCard({
  stepNumber,
  kindLabel,
  title,
  subtitle,
  iconName,
  iconColor,
  iconBackground,
  configured,
  selected,
  onSelect,
}: AgentAutomationStepCardProps) {
  const resolvedIconColor = iconColor ?? 'var(--ink-base)';
  const resolvedIconBg = iconBackground ?? 'var(--sky-lighter)';

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      $selected={selected}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
    >
      <StepIndex>{stepNumber}</StepIndex>
      <IconTile style={{ background: resolvedIconBg }}>
        <Icon name={iconName} color={resolvedIconColor} size={22} />
      </IconTile>
      <Body>
        <KindLabel>{kindLabel}</KindLabel>
        <TitleRow>
          <Text size="medium" weight="bold" color="var(--ink-darkest)">
            {title}
          </Text>
          <StatusPill $configured={configured}>
            <Icon
              name={configured ? 'check_circle' : 'error'}
              color={configured ? 'var(--primary-base)' : 'var(--amber-base, #d98e2b)'}
              size={12}
            />
            <Text size="tiny" weight="medium" color={configured ? 'var(--primary-base)' : 'var(--amber-base, #d98e2b)'}>
              {configured ? 'Configured' : 'Needs setup'}
            </Text>
          </StatusPill>
        </TitleRow>
        {subtitle && (
          <Text size="small" color="var(--ink-base)">
            {subtitle}
          </Text>
        )}
      </Body>
    </Card>
  );
}

interface AgentAutomationStepConnectorProps {
  onAdd?: () => void;
  addLabel?: string;
}

export function AgentAutomationStepConnector({ onAdd, addLabel }: AgentAutomationStepConnectorProps) {
  return (
    <ConnectorWrapper>
      <ConnectorLine />
      {onAdd && (
        <AddButton type="button" onClick={onAdd} aria-label={addLabel ?? 'Add step'}>
          <Icon name="add" color="var(--ink-base)" size={16} />
          {addLabel && (
            <Text size="tiny" weight="medium" color="var(--ink-dark)">
              {addLabel}
            </Text>
          )}
        </AddButton>
      )}
    </ConnectorWrapper>
  );
}

const Card = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: var(--base-white);
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--secondary-base)' : 'var(--sky-light)')};
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-shadow: ${({ $selected }) =>
    $selected ? '0 0 0 4px rgba(87, 92, 229, 0.15)' : '0 1px 2px rgba(15, 23, 42, 0.04)'};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? 'var(--secondary-base)' : 'var(--sky-base)')};
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const StepIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--sky-lighter);
  color: var(--ink-base);
  font-size: 11px;
  font-weight: 700;
`;

const IconTile = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KindLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-lighter);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatusPill = styled.span<{ $configured: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $configured }) => ($configured ? 'rgba(35, 193, 107, 0.12)' : 'rgba(217, 142, 43, 0.12)')};
`;

const ConnectorWrapper = styled.div`
  height: 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ConnectorLine = styled.div`
  width: 0;
  height: 100%;
  border-left: 2px dashed var(--sky-base);
`;

const AddButton = styled.button`
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1.5px solid var(--sky-base);
  background: var(--base-white);
  cursor: pointer;
  color: var(--ink-dark);
  font-size: 12px;
  font-weight: 600;
  transition: border-color 0.12s, background 0.12s;

  &:hover {
    border-color: var(--secondary-base);
    background: var(--sky-lightest);
  }
`;

export type { AgentAutomationStepCardProps, AgentAutomationStepConnectorProps, StepKind };
